import { WorkflowStageSelector } from './workflow';

export type LeaveSession = 'FULL_DAY' | 'FIRST_HALF' | 'SECOND_HALF';

export type LeaveRequestStatus =
  | 'PENDING'
  | 'APPROVAL_CONFIGURATION_REQUIRED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED';

// What the leave currently means once Holidays are applied, separate from its
// approval status. NO_LONGER_REQUIRED: a Pending / Approval Configuration
// Required request whose every date lost its charge — approval is blocked.
// APPROVED_NO_LEAVE_CHARGED: an Approved request whose charge is now zero.
export type LeaveOperationalOutcome =
  | 'ACTIVE'
  | 'NO_LONGER_REQUIRED'
  | 'APPROVED_NO_LEAVE_CHARGED'
  | 'CLOSED';

// ACTION_REQUIRED marks a Holiday removal that needs an amend/resolve decision
// on its Holiday impact case (operationalOutcome stays ACTIVE meanwhile).
export type LeaveHolidayImpactState =
  | 'NONE'
  | 'RECALCULATION_PENDING'
  | 'ACTION_REQUIRED'
  | 'DEFERRED_BY_CANCELLATION'
  | 'RESOLVED';

// Approval (canApprove / canOverrideApprove) is blocked in exactly these cases.
export const isLeaveApprovalBlocked = (request: {
  operationalOutcome?: LeaveOperationalOutcome | null;
  holidayImpactState?: LeaveHolidayImpactState | null;
}): boolean =>
  request.operationalOutcome === 'NO_LONGER_REQUIRED' ||
  request.holidayImpactState === 'RECALCULATION_PENDING' ||
  request.holidayImpactState === 'ACTION_REQUIRED';

export type LeaveStageStatus =
  | 'PENDING'
  | 'CURRENT'
  | 'APPROVED'
  | 'REJECTED'
  | 'SKIPPED'
  | 'INACTIVE';

export type LeaveStageSkipReason = 'NO_REPORTING_MANAGER';

export interface LeaveRequestStage {
  order: number;
  selector: WorkflowStageSelector;
  status: LeaveStageStatus;
  assigneeUuid: string | null;
  assigneeName: string | null;
  skipReason: LeaveStageSkipReason | null;
}

// The latest cancellation attempt on a request, regardless of outcome — the
// leave keeps its own status independently (e.g. an APPROVED leave under
// cancellation review still reads status: 'APPROVED').
export interface LeaveCancellationSummary {
  uuid: string;
  status: LeaveCancellationStatus;
  reason: string;
  decisionReason: string | null;
  decidedAt: string | null;
}

// §11a list-row shape — no stages[]/history, just the current stage.
export interface LeaveRequestListItem {
  uuid: string;
  employeeUuid: string;
  employeeName: string;
  // Only present on /people/leave-requests/history rows, not the review queue.
  employeeDesignation?: string;
  // Requested from backend but not yet returned as of this writing — falls
  // back to initials wherever rendered until it's actually present.
  profileImageUrl?: string | null;
  leaveAllocationUuid: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  // Exact working dates in order — empty means unavailable (older snapshot),
  // NOT a zero-day request. Fall back to fromDate–toDate + chargeableDays.
  chargeableDates: string[];
  session: LeaveSession;
  chargeableDays: number;
  reason: string;
  status: LeaveRequestStatus;
  currentStage: LeaveRequestStage | null;
  cancellationRequest: LeaveCancellationSummary | null;
  version: number;
  createdAt: string;
  // The leave's current result after Holiday changes — what lists show as its
  // dates and days. chargeableDays/chargeableDates above stay the original
  // request. An empty effectiveChargeableDates means no leave date remains
  // (unlike chargeableDates, where empty means unavailable).
  effectivePaidDays?: number | null;
  effectiveUnpaidDays?: number | null;
  effectiveCoverageDays?: number | null;
  effectiveChargeableDates?: string[] | null;
  operationalOutcome?: LeaveOperationalOutcome | null;
  holidayImpactState?: LeaveHolidayImpactState | null;
  canApprove: boolean;
  canReject: boolean;
  canOverride: boolean;
  canCancel: boolean;
  canRequestCancellation: boolean;
  hasAttachment: boolean;
  // True for an organisation-authority (ORG_OWNER/ORG_ADMIN) decision — a
  // non-blank reason must be collected before Approve/Reject. False for a
  // normal current-stage decision.
  decisionReasonRequired: boolean;
}

export type LeaveAttachmentMediaType = 'DOCUMENT_PDF' | 'DOCUMENT_WORD' | 'IMAGE';

export interface LeaveAttachment {
  mediaUuid: string;
  originalFileName: string;
  mediaType: LeaveAttachmentMediaType;
  mediaSize: number;
  previewUrl: string;
  downloadUrl: string;
}

// One decision recorded against a request — a normal stage approve/reject or
// an HR Override, in the order they happened. Confirmed from a live response;
// not in the original API doc, which only showed `"history": []`.
export interface LeaveRequestHistoryEntry {
  actorUuid: string;
  actorName: string;
  actedAt: string;
  decision: 'APPROVED' | 'REJECTED';
  reason: string | null;
  source: string;
}

export interface LeaveRequest {
  uuid: string;
  employeeUuid: string;
  employeeName: string;
  // Requested from backend but not yet returned as of this writing — falls
  // back to initials wherever rendered until it's actually present.
  profileImageUrl?: string | null;
  requesterUuid: string;
  leaveAllocationUuid: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  session: LeaveSession;
  chargeableDays: number;
  chargeableDates: string[];
  reason: string;
  status: LeaveRequestStatus;
  cancellationRequest: LeaveCancellationSummary | null;
  workflowUuid: string;
  workflowRevision: number;
  hrOverrideEnabled: boolean;
  reportingManagerUuid: string | null;
  reportingManagerName: string | null;
  version: number;
  stages: LeaveRequestStage[];
  history: LeaveRequestHistoryEntry[];
  operationalOutcome?: LeaveOperationalOutcome | null;
  holidayImpactState?: LeaveHolidayImpactState | null;
  // Render actions from these — never infer from role/status alone. A
  // Pending status doesn't mean approvable: canApprove and
  // canOverrideApprove are false while approval is blocked.
  canApprove: boolean;
  canReject: boolean;
  // Shows the HR Override section and drives Reject Override.
  canOverride: boolean;
  // Drives Approve Override — canOverride AND approval not blocked.
  canOverrideApprove: boolean;
  canCancel: boolean;
  canRequestCancellation: boolean;
  // True for an organisation-authority (ORG_OWNER/ORG_ADMIN) decision — a
  // non-blank reason must be collected before Approve/Reject. False for a
  // normal current-stage decision.
  decisionReasonRequired: boolean;
  attachment: LeaveAttachment | null;
}

export interface SubmitLeaveRequestPayload {
  leaveAllocationUuid: string;
  fromDate: string;
  toDate: string;
  session: LeaveSession;
  reason: string;
  attachmentMediaUuid?: string;
  // From the latest calculate for exactly these values, sent back unchanged.
  // Required for leave ending on or after the organisation's token cutover.
  calculationToken?: string;
}

export interface CalculateLeaveRequestPayload {
  fromDate: string;
  toDate: string;
  session: LeaveSession;
  // The selected Leave Type. Omitted only by the date picker's month
  // preview, which just greys out non-working dates — without it the
  // response never allows submission.
  leaveAllocationUuid?: string;
}

export interface CalculateLeaveRequestResponse {
  chargeableDays: number;
  chargeableDates: string[];
  // Whether these exact values can be submitted; when false,
  // nonSubmissionReason says why.
  canSubmit?: boolean;
  // Single-use; null when canSubmit is false. Recalculate once it expires or
  // after any failed submit.
  calculationToken?: string | null;
  // ISO instant — about 15 minutes after the calculation.
  calculationTokenExpiresAt?: string | null;
  nonSubmissionReason?: string | null;
}

export type OverrideDecision = 'APPROVED' | 'REJECTED';

// NO_LONGER_REQUIRED: a Holiday change removed the leave's remaining days, so
// there's nothing left to cancel — closed without a reviewer decision.
export type LeaveCancellationStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'NO_LONGER_REQUIRED';

export const LEAVE_CANCELLATION_STATUS_LABEL: Record<LeaveCancellationStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  NO_LONGER_REQUIRED: 'No longer required',
};

// Who actually decided a cancellation — null while still PENDING, then set
// once terminal. EXPIRED is always SYSTEM (the leave-start cutoff passed
// with no reviewer decision). Never infer this from the deciding user's
// current role — a since-changed role wouldn't reflect what was true then.
export type LeaveCancellationDecisionSource = 'HR_REVIEW' | 'ORG_AUTHORITY' | 'SYSTEM';

export interface LeaveCancellationRequest {
  uuid: string;
  leaveRequestUuid: string;
  employeeUuid: string;
  employeeName: string;
  leaveAllocationUuid: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  chargeableDays: number;
  requesterUuid: string;
  reason: string;
  status: LeaveCancellationStatus;
  decidedByUuid: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  decidedAt: string | null;
  decisionSource: LeaveCancellationDecisionSource | null;
  version: number;
  canApprove: boolean;
  canReject: boolean;
  // True for an Organisation Authority decision — a non-blank reason must be
  // collected before Approve/Reject. False for a normal HRM Admin review.
  decisionReasonRequired: boolean;
}

const LEAVE_CANCELLATION_DECISION_SOURCE_LABEL: Record<LeaveCancellationDecisionSource, string> = {
  HR_REVIEW: 'HR Review',
  ORG_AUTHORITY: 'Organisation Authority',
  SYSTEM: 'System',
};

export const getCancellationDecisionSourceLabel = (
  source: LeaveCancellationDecisionSource | null | undefined,
): string | null => (source && LEAVE_CANCELLATION_DECISION_SOURCE_LABEL[source]) || null;

// Standard Spring Data Page<T> shape.
export interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface LeaveListParams {
  status?: LeaveRequestStatus[];
  leaveAllocationUuid?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface ReviewQueueParams {
  /** Employee name, matched across the whole queue — not just one page. */
  search?: string;
  page?: number;
  size?: number;
  sort?: string;
}

// GET /people/leave-requests/history — separate from the review queue, which
// only ever returns still-actionable pending items. Omitting status returns
// APPROVED, REJECTED, and CANCELLED requests.
export interface LeaveHistoryParams {
  search?: string;
  employeeUuid?: string;
  leaveAllocationUuid?: string;
  status?: Array<'APPROVED' | 'REJECTED' | 'CANCELLED'>;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export type LeaveManagementErrorCode =
  | 'LEAVE_WORKFLOW_NOT_CONFIGURED'
  | 'NO_ELIGIBLE_HR_ADMIN'
  | 'INSUFFICIENT_LEAVE_BALANCE'
  | 'OVERLAPPING_LEAVE_REQUEST'
  | 'CROSS_LEAVE_YEAR_NOT_ALLOWED'
  | 'STALE_RECORD_VERSION'
  | 'REQUEST_ALREADY_ACTIONED'
  | 'APPROVAL_CONFIGURATION_REQUIRED'
  | 'LEAVE_TYPE_NOT_EFFECTIVE'
  | 'WORKING_DAY_POLICY_REQUIRED'
  | 'NO_ELIGIBLE_CANCELLATION_REVIEWER'
  | 'LEAVE_CALCULATION_CHANGED';

// Fallback copy per the spec's recommended-handling table — used only when
// the API doesn't already supply a human-readable message for the code.
export const LEAVE_ERROR_FALLBACKS: Record<LeaveManagementErrorCode, string> = {
  LEAVE_WORKFLOW_NOT_CONFIGURED: 'No Leave Approval workflow is active — leave cannot be submitted right now.',
  NO_ELIGIBLE_HR_ADMIN: 'No eligible HR Admin is available to process this.',
  INSUFFICIENT_LEAVE_BALANCE: 'Insufficient leave balance for the selected date range.',
  OVERLAPPING_LEAVE_REQUEST: 'An overlapping leave request already exists for these dates.',
  CROSS_LEAVE_YEAR_NOT_ALLOWED: 'This request spans more than one Leave Year — submit separate requests per Leave Year.',
  STALE_RECORD_VERSION: 'This was updated elsewhere. Refresh and try again.',
  REQUEST_ALREADY_ACTIONED: 'Another action was already taken on this. Refresh to see the latest state.',
  APPROVAL_CONFIGURATION_REQUIRED: 'This request is blocked pending approval configuration.',
  LEAVE_TYPE_NOT_EFFECTIVE: 'This Leave Type cannot currently be requested.',
  WORKING_DAY_POLICY_REQUIRED: 'No working-day policy is set up for you — contact HR before requesting leave.',
  NO_ELIGIBLE_CANCELLATION_REVIEWER: 'No eligible reviewer is currently available for this cancellation request.',
  LEAVE_CALCULATION_CHANGED: 'This leave has changed after a Holiday update and can no longer be approved. Refresh to see the latest state.',
};

// Codes whose copy here replaces the API's own message: the backend's
// wording for these reads badly ("Insufficient Available to Request balance.").
const LEAVE_ERROR_COPY_OVERRIDES: LeaveManagementErrorCode[] = ['INSUFFICIENT_LEAVE_BALANCE'];

export const getLeaveErrorMessage = (error: any, fallback: string = 'Something went wrong. Please try again.'): string => {
  // errorCode is where the shared axios interceptor puts the backend's code
  // (see normalizeApiError) — the raw response is gone by the time it's here.
  const code: LeaveManagementErrorCode | undefined =
    error?.errorCode ?? error?.response?.data?.code;
  if (code && LEAVE_ERROR_COPY_OVERRIDES.includes(code)) return LEAVE_ERROR_FALLBACKS[code];
  const apiMessage = error?.response?.data?.message;
  if (apiMessage) return apiMessage;
  if (code && LEAVE_ERROR_FALLBACKS[code]) return LEAVE_ERROR_FALLBACKS[code];
  return error?.message || fallback;
};

// Only the sources that need a distinct, explicit label in the UI — a normal
// in-flow stage decision isn't called out with a badge at all. ORG_AUTHORITY
// must never be shown as "HR Override" (they're mutually exclusive actors).
const LEAVE_HISTORY_SOURCE_LABEL: Record<string, string> = {
  ORG_AUTHORITY: 'Organisation Authority',
  HR_OVERRIDE: 'HR Override',
};

export const getLeaveHistorySourceLabel = (source: string | null | undefined): string | null =>
  (source && LEAVE_HISTORY_SOURCE_LABEL[source]) || null;

// Renders the Figma "24, 27, 28, 29, 30 April" style list from exact
// chargeable dates, or null when unavailable (caller should fall back to the
// fromDate–toDate range + chargeableDays instead of guessing).
export const formatChargeableDates = (dates: string[]): string | null => {
  if (!dates || dates.length === 0) return null;
  return dates
    .map((d) => {
      const [, month, day] = d.split('-');
      const monthName = new Date(Number(d.slice(0, 4)), Number(month) - 1, 1).toLocaleString('en-US', {
        month: 'short',
      });
      return `${Number(day)} ${monthName}`;
    })
    .join(', ');
};
